import React from 'react';
import clsx from 'clsx';
import {type RowData} from '@tanstack/react-table';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import useSharedRef from '@netx/core-components/hooks/useSharedRef';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {splitEventHandler} from '@netx/core-next/utils';
import {tableCellClasses} from './tableCellClasses';
import {
	getTableRowUtilityClass,
	TABLE_ROW_CLASS_NAME,
	tableRowClasses,
	type TableRowClasses,
} from './tableRowClasses';

type TableRowOwnerState = WithClasses<
	TableRowClasses,
	{
		isActive?: boolean;
		isSelected?: boolean;
	}
>;

const useUtilityClasses = (ownerState: TableRowOwnerState) => {
	const {classes, isActive, isSelected} = ownerState;
	const slots = {
		root: ['root', isActive && 'isActive', isSelected && 'isSelected'],
	};
	return composeClasses(slots, getTableRowUtilityClass, classes);
};

const TableRowRoot = styled('div', {
	name: TABLE_ROW_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => {
	const {isActive, isSelected} = ownerState as TableRowOwnerState;
	return {
		display: 'flex',
		flex: '0 0 auto',
		borderBottom: `1px solid ${theme.palette.custom.border}`,
		minWidth: '100%',
		position: 'relative',
		outline: 'none',
		'&::after': {
			...theme.mixins.absoluteFill,
			backgroundColor: isSelected
				? theme.palette.secondary.light
				: isActive
				? theme.palette.action.hover
				: undefined,
			content: '""',
			pointerEvents: 'none',
			opacity: isSelected
				? isActive
					? theme.palette.action.selectedOpacity +
					  theme.palette.action.hoverOpacity
					: theme.palette.action.selectedOpacity
				: undefined,
			// live on top of frozen cells
			zIndex: 4,
		},
		[`.${tableCellClasses.isUtilityCell} + .${tableCellClasses.isDataCell}`]: {
			paddingLeft: 0,
		},
		[`.${tableCellClasses.isDragHandleCell} + .${tableCellClasses.isSelectionCell}`]:
			{
				paddingLeft: 0,
			},
	};
});

export interface TableRowProps<TData extends RowData>
	extends WithClasses<
		TableRowClasses,
		React.PropsWithChildren<
			React.DetailedHTMLProps<
				React.HTMLAttributes<HTMLDivElement>,
				HTMLDivElement
			>
		>
	> {
	index: number;
	isActive?: boolean;
	isSelected?: boolean;
	onRowClick?: (event: React.MouseEvent) => void;
}

export const TableRowPrimitive = <TData extends RowData>(
	{
		classes: _classes,
		className,
		index,
		isActive,
		isSelected,
		onMouseDown,
		onMouseUp,
		onRowClick,
		...props
	}: TableRowProps<TData>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const ownerState: TableRowOwnerState = {
		classes: _classes,
		isActive,
		isSelected,
	};

	const classes = useUtilityClasses(ownerState);

	const rootRef = useSharedRef<HTMLDivElement | null>(null, [ref]);

	const mouseDownAt = React.useRef<number>(0);

	// to prevent a click from firing when the user is selecting text / dragging
	// we will listen for mouse down and mouse up - if the time between the two
	// is less than 300ms then we will fire the click event

	const handleMouseDown = useConstCallback(
		splitEventHandler(onMouseDown, (event) => {
			if (event.button || event.defaultPrevented || event.ctrlKey) return;
			mouseDownAt.current = Date.now();
		}),
	);

	const handleMouseUp = useConstCallback(
		splitEventHandler(onMouseUp, (event) => {
			if (event.defaultPrevented || event.ctrlKey) return;
			if (Date.now() - mouseDownAt.current < 300) {
				onRowClick?.(event);
			}
		}),
	);

	return (
		<TableRowRoot
			role="row"
			data-row-index={index}
			aria-selected={isSelected}
			{...props}
			ref={rootRef}
			className={clsx(classes.root, className)}
			ownerState={ownerState}
			onMouseDown={handleMouseDown}
			onMouseUp={handleMouseUp}
		/>
	);
};

export const TableRow = React.memo(
	React.forwardRef(TableRowPrimitive),
) as typeof TableRowPrimitive;

export default TableRow;
